import type { UploadedFile } from '@/shared/components/form-fields/types';
import { FieldComponents } from '@/shared/components/form-fields/types';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { toaster } from '@/shared/ui/toaster.component';
import { Button, Spinner } from '@noorden-insurtech/alba-design-system';
import { formOptions } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { InspectionStatus } from '../../../../shared/enums/inspection-status.enum';
import { useCreateInspection } from '../../hooks/use-create-inspection.hook';
import {
  useFollowUpInspectionForm,
  withForm,
} from '../../hooks/use-follow-up-inspection-form.hook';
import { useFollowUpInspection } from '../../hooks/use-follow-up-inspection.hook';
import { useInspectionStatuses } from '../../hooks/use-inspection-statuses.hook';
import { inspectionStatusConfig } from '../inspections-table-columns';
import type { Step2InspectionState } from './step-2-inspection';

const EXCLUDED_FROM_FOLLOWUP_DROPDOWN: ReadonlySet<string> = new Set([
  InspectionStatus.UnderReview,
  InspectionStatus.Accepted,
  InspectionStatus.Completed,
  InspectionStatus.Pending,
]);

const FOLLOWUP_DROPDOWN_LABEL_OVERRIDES: Partial<
  Record<InspectionStatus, string>
> = {
  [InspectionStatus.Dismissed]: 'Dispensar Inspeção',
};

const PARECER_TECNICO_STATUSES: ReadonlySet<string> = new Set([
  InspectionStatus.UnderReview,
  InspectionStatus.Frustrated,
  InspectionStatus.Canceled,
]);

const TECHNICAL_OPINION_REQUIRED_MESSAGE = 'O parecer técnico é obrigatório.';

const OBSERVATION_REQUIRED_MESSAGE =
  'A observação é obrigatória ao dispensar inspeção.';

type FollowUpInspectionFormValues = {
  status: string;
  observations: string;
  attachments: UploadedFile[];
};

const GO_TO_ANALYSIS_STATUSES: ReadonlySet<string> = new Set([
  InspectionStatus.InProgress,
  InspectionStatus.Frustrated,
  InspectionStatus.UnderReview,
  InspectionStatus.Dismissed,
  InspectionStatus.Canceled,
]);

interface Step2Tab1OverviewProps {
  policyId: string;
  inspectionId: string;
  // eslint-disable-next-line no-unused-vars
  onStepChange: (_state: Partial<Step2InspectionState>) => void;
  canWrite: boolean;
  hasWritePermission: boolean;
  latestRecordStatus?: InspectionStatus;
}

export function Step2Tab1Overview({
  policyId,
  inspectionId,
  onStepChange,
  canWrite,
  hasWritePermission,
  latestRecordStatus,
}: Step2Tab1OverviewProps) {
  const navigate = useNavigate();
  const [isStatusLocked, setIsStatusLocked] = useState(false);

  const { data: inspectionStatuses, isLoading: isLoadingStatuses } =
    useInspectionStatuses();

  const createInspection = useCreateInspection();
  const followUpMutation = useFollowUpInspection(policyId, inspectionId);

  const hasTechnicalOpinion =
    latestRecordStatus === InspectionStatus.UnderReview;
  const isTerminalStatus = !!(
    latestRecordStatus &&
    [
      InspectionStatus.Frustrated,
      InspectionStatus.Canceled,
      InspectionStatus.Accepted,
      InspectionStatus.Dismissed,
      InspectionStatus.Completed,
    ].includes(latestRecordStatus)
  );
  const isFormLocked = hasTechnicalOpinion || isTerminalStatus;

  const isPending = followUpMutation.isPending;

  const mainForm = useFollowUpInspectionForm({
    defaultValues: {
      status: latestRecordStatus ?? '',
      observations: '',
      attachments: [],
    },
    onSubmit: async ({ value }: { value: FollowUpInspectionFormValues }) => {
      try {
        await followUpMutation.mutateAsync({
          status: value.status,
          description: value.observations,
          attachments: value.attachments,
        });

        if (value.status === InspectionStatus.Dismissed) {
          toaster.create({
            type: 'success',
            title: 'Inspeção dispensada',
            description:
              'O status foi atualizado. Clique em "Ir para decisão" para prosseguir.',
          });

          mainForm.reset();
          return;
        }

        toaster.create({
          type: 'success',
          title: 'Alteração registrada',
          description: 'O status da inspeção foi atualizado com sucesso.',
        });

        mainForm.reset();
        setIsStatusLocked(false);
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao registrar alteração',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    },
  });

  const statusOptions =
    inspectionStatuses
      ?.filter(
        status =>
          !EXCLUDED_FROM_FOLLOWUP_DROPDOWN.has(status.name.toLowerCase())
      )
      .map(status => {
        const key = status.name.toLowerCase() as InspectionStatus;
        const label =
          FOLLOWUP_DROPDOWN_LABEL_OVERRIDES[key] ??
          inspectionStatusConfig[key]?.label ??
          status.name;
        return { value: key, label };
      }) || [];

  const showGoToAnalysis =
    !!latestRecordStatus &&
    GO_TO_ANALYSIS_STATUSES.has(latestRecordStatus) &&
    !isStatusLocked;
  const showCreateInspectionButton =
    latestRecordStatus === InspectionStatus.Frustrated ||
    latestRecordStatus === InspectionStatus.Canceled;

  const handleCreateInspection = () => {
    if (!policyId.trim()) return;

    createInspection.mutate(policyId, {
      onSuccess: result => {
        toaster.create({
          type: 'success',
          title: 'Nova inspeção criada',
          description: 'Redirecionando para a inspeção...',
        });
        void navigate({
          to: '/policies/$policyId/inspections/$inspectionId',
          params: {
            policyId: result.policyId,
            inspectionId: result.inspectionId,
          },
          search: { tab: 'inspection' },
        });
      },
      onError: error => {
        toaster.create({
          type: 'error',
          title: 'Erro ao criar nova inspeção',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      },
    });
  };

  const handleGoToAnalysis = () => {
    if (latestRecordStatus === InspectionStatus.InProgress) {
      mainForm.setFieldValue('status', InspectionStatus.UnderReview);
      setIsStatusLocked(true);
      return;
    }
    onStepChange({ step: 'decision', decision: latestRecordStatus ?? null });
  };

  return (
    <div className='space-y-8'>
      <div className='flex flex-col gap-8 bg-white shadow-xl rounded-2xl py-12 px-8'>
        <p>Acompanhamento da Inspeção</p>

        {isLoadingStatuses ? (
          <div className='flex justify-center items-center py-8'>
            <Spinner size='lg' />
          </div>
        ) : (
          <FollowUpInspectionForm
            form={mainForm}
            statusOptions={statusOptions}
            disabled={!canWrite || isPending || isFormLocked}
            isStatusLocked={isStatusLocked}
          />
        )}
      </div>

      <div className='flex flex-row gap-4 justify-between items-center'>
        <Button
          variant='ghost'
          size='md'
          onClick={() => void navigate({ to: '/inspections' })}
        >
          <ArrowLeft className='size-4' />
          Voltar
        </Button>

        <div className='flex flex-row gap-4'>
          {hasWritePermission && showCreateInspectionButton && (
            <Button
              variant='primary'
              size='md'
              onClick={handleCreateInspection}
              disabled={createInspection.isPending}
              loading={createInspection.isPending}
            >
              Criar nova inspeção
            </Button>
          )}

          {canWrite && !isFormLocked && (
            <Button
              variant='primary'
              size='md'
              onClick={mainForm.handleSubmit}
              disabled={isPending}
              loading={isPending}
            >
              Registrar alteração
            </Button>
          )}

          {hasWritePermission && showGoToAnalysis && (
            <Button
              variant='primary'
              size='md'
              onClick={handleGoToAnalysis}
              disabled={isPending}
            >
              {latestRecordStatus === InspectionStatus.InProgress
                ? 'Ir para análise'
                : 'Ir para decisão'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const formOpts = formOptions({
  defaultValues: {
    status: '',
    observations: '',
    attachments: [],
  },
});

const FollowUpInspectionForm = withForm({
  ...formOpts,
  props: {
    statusOptions: [] as Array<{ value: string; label: string }>,
    disabled: false,
    isStatusLocked: false,
  },
  render: ({
    form,
    statusOptions,
    disabled,
    isStatusLocked,
  }: {
    form: any;
    statusOptions: Array<{ value: string; label: string }>;
    disabled: boolean;
    isStatusLocked: boolean;
  }) => {
    return (
      <div className='flex flex-col gap-4'>
        <form.AppField
          name='status'
          validators={{
            onChange: ({ value }: { value: string | undefined }) => {
              if (!value) return 'O status é obrigatório';
              return undefined;
            },
          }}
          children={(field: FieldComponents) => (
            <field.SelectField
              label='Status'
              placeholder='Selecione o status'
              required
              options={
                isStatusLocked
                  ? [
                    {
                      value: InspectionStatus.UnderReview,
                      label: 'Em Análise',
                    },
                  ]
                  : statusOptions
              }
              disabled={disabled || isStatusLocked}
            />
          )}
        />

        <form.Subscribe
          selector={(state: any) =>
            state?.values?.status ?? InspectionStatus.InProgress
          }
          children={(status: string) => {
            const isPareciTecnico = PARECER_TECNICO_STATUSES.has(status);
            const isDismissed = status === InspectionStatus.Dismissed;
            const isRequired = isPareciTecnico || isDismissed;

            const fieldLabel = isPareciTecnico
              ? 'Parecer Técnico'
              : 'Observação';
            const fieldPlaceholder = isPareciTecnico
              ? 'Digite o parecer técnico...'
              : 'Digite observações sobre a inspeção...';

            return (
              <form.AppField
                name='observations'
                validators={{
                  onChange: ({ value }: { value: string | undefined }) => {
                    if (isPareciTecnico && !value?.trim()) {
                      return TECHNICAL_OPINION_REQUIRED_MESSAGE;
                    }
                    if (isDismissed && !value?.trim()) {
                      return OBSERVATION_REQUIRED_MESSAGE;
                    }
                    return undefined;
                  },
                  onSubmit: ({ value }: { value: string | undefined }) => {
                    if (isPareciTecnico && !value?.trim()) {
                      return TECHNICAL_OPINION_REQUIRED_MESSAGE;
                    }
                    if (isDismissed && !value?.trim()) {
                      return OBSERVATION_REQUIRED_MESSAGE;
                    }
                    return undefined;
                  },
                }}
                children={(field: FieldComponents) => (
                  <field.TextareaField
                    label={fieldLabel}
                    placeholder={fieldPlaceholder}
                    required={isRequired}
                    rows={5}
                    maxLength={FIELD_MAX_LENGTHS.inspectionRecordDescription}
                    disabled={disabled}
                  />
                )}
              />
            );
          }}
        />

        <form.AppField
          name='attachments'
          children={(field: FieldComponents) => (
            <field.UploadField
              label='Anexos'
              accept='application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png'
              helperText='Formatos aceitos: PDF, DOC, DOCX, JPG, JPEG, PNG'
              multiple
              disabled={disabled}
            />
          )}
        />
      </div>
    );
  },
});
